export const AUCTION_CONDITION_IDS = [
  "certified-plus",
  "certified",
  "tested",
  "repair-object",
] as const;

export type AuctionConditionId = (typeof AUCTION_CONDITION_IDS)[number];

export type AuctionCondition = {
  id: AuctionConditionId;
  labelAr: string;
  labelEn: string;
  line: string;
  icon: "award" | "badge-check" | "clipboard-check" | "wrench";
  bullets: readonly string[];
  readMoreLabel: string;
};

export const AUCTION_CONDITION_REQUIRED_MESSAGE =
  "اختر حالة السيارة قبل نشرها للمزاد";

function grade(
  id: AuctionConditionId,
  labelAr: string,
  labelEn: string,
  icon: AuctionCondition["icon"],
  bullets: readonly string[],
): AuctionCondition {
  const line = `${labelAr} · ${labelEn}`;
  return {
    id,
    labelAr,
    labelEn,
    line,
    icon,
    bullets,
    readMoreLabel: `اقرأ المزيد عن ${line} هنا`,
  };
}

export const AUCTION_CONDITIONS: readonly AuctionCondition[] = [
  grade("certified-plus", "معتمد بلس", "Certified Plus", "award", [
    "السيارة أحدث من 8 سنوات",
    "بحالة تقنية ووظيفية جيدة",
    "عداد لا يتجاوز 12,000 كم",
    "الهيكل والمقصورة أفضل من الحالة المعتادة",
  ]),
  grade("certified", "معتمد", "Certified", "badge-check", [
    "خضعت لفحص أوتوبلس",
    "عمرها لا يزيد عن 8 سنوات",
    "العداد لا يتجاوز 120,000 كم",
    "لا ملاحظات على مجموعة الحركة، ولا ملاحظات مؤثرة على الفرامل أو التقنية أو التجهيزات",
    "عمق نقش الإطارات مقبول",
  ]),
  grade("tested", "مفحوص", "Tested", "clipboard-check", [
    "خضعت لفحص أوتوبلس",
    "سيارة مستعملة بحالة عمل، مع آثار استخدام",
    "قد تكون أقدم أو بعداد أعلى من شروط «معتمد»",
    "قد تحمل ملاحظات تحتاج متابعة بعد الشراء",
  ]),
  grade("repair-object", "بحاجة لإصلاح", "Repair Object", "wrench", [
    "تحتاج إصلاحاً أو عملاً واسعاً",
    "تُباع كما هي",
    "فيها أعطال تؤثر على الوظيفة أو السلامة أو قابلية القيادة",
    "تُناسب من يستطيع الإصلاح بعد الشراء",
  ]),
];

export function auctionGradeById(
  id: string | null | undefined,
): AuctionCondition | null {
  if (!id) return null;
  return AUCTION_CONDITIONS.find((grade) => grade.id === id) ?? null;
}

export function auctionMayBePublic(
  saleType: "auction" | "fixed" | string,
  gradeId: string | null | undefined,
): boolean {
  if (saleType !== "auction") return true;
  return auctionGradeById(gradeId) != null;
}

export function assertAuctionGradeForSale(
  saleType: string,
  gradeId: string | null | undefined,
): AuctionConditionId | null {
  if (saleType !== "auction") return null;
  const grade = auctionGradeById(gradeId);
  if (!grade) throw new Error(AUCTION_CONDITION_REQUIRED_MESSAGE);
  return grade.id;
}
